import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import PlatformOwnerAppShell from '../components/PlatformOwnerAppShell';
import StepUpAuthModal, { getValidStepUpToken, PreviewSecurityBanner } from '../components/StepUpAuthModal';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  ArrowRightLeft, AlertTriangle, ShieldCheck, Search,
  CheckCircle, XCircle, Crown, User, Mail, Loader2
} from 'lucide-react';

export default function TransferOwnership() {
  const { api, setToken, setUser, logout } = useAuth();
  const [currentOwner, setCurrentOwner] = useState(null);
  const [loading, setLoading] = useState(true);

  // Target lookup
  const [targetEmail, setTargetEmail] = useState('');
  const [validating, setValidating] = useState(false);
  const [targetUser, setTargetUser] = useState(null);
  const [targetError, setTargetError] = useState('');

  // Confirmation flow
  const [showConfirm, setShowConfirm] = useState(false);
  const [showStepUp, setShowStepUp] = useState(false);
  const [transferring, setTransferring] = useState(false);
  const [transferComplete, setTransferComplete] = useState(false);
  const [transferResult, setTransferResult] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/platform-owner/transfer-ownership/current');
        setCurrentOwner(res.data.current_owner);
      } catch { toast.error('Failed to load owner info'); }
      setLoading(false);
    })();
  }, [api]);

  const handleLookup = async () => {
    if (!targetEmail.trim()) return;
    setValidating(true);
    setTargetUser(null);
    setTargetError('');
    try {
      const res = await api.get(`/platform-owner/transfer-ownership/validate-target?email=${encodeURIComponent(targetEmail.trim())}`);
      if (res.data.valid) {
        setTargetUser(res.data.user);
      } else {
        setTargetError(res.data.reason);
      }
    } catch (e) {
      setTargetError(e.response?.data?.detail || 'Failed to validate user');
    }
    setValidating(false);
  };

  const handleInitiateTransfer = () => {
    if (!targetUser) return;
    setShowConfirm(true);
  };

  const handleConfirmTransfer = () => {
    setShowConfirm(false);
    const existing = getValidStepUpToken();
    if (existing) {
      executeTransfer(existing);
    } else {
      setShowStepUp(true);
    }
  };

  const executeTransfer = async (stepUpToken) => {
    setTransferring(true);
    try {
      const res = await api.post('/platform-owner/transfer-ownership', {
        target_email: targetUser.email,
        step_up_token: stepUpToken,
      });
      setTransferComplete(true);
      setTransferResult(res.data);
      // Update the local token so old owner can still navigate
      if (res.data.new_token) {
        localStorage.setItem('token', res.data.new_token);
        setToken(res.data.new_token);
      }
      toast.success('Platform ownership transferred successfully');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Transfer failed. No changes were made.');
    }
    setTransferring(false);
  };

  const roleLabel = (roles) => {
    if (!roles || !roles.length) return 'Customer';
    if (roles.includes('super_admin')) return 'Super Admin';
    if (roles.includes('admin')) return 'Admin';
    if (roles.includes('owner')) return 'Host';
    return 'Customer';
  };

  if (transferComplete) {
    return (
      <PlatformOwnerAppShell activeId="transfer-ownership" title="Transfer Ownership">
        <div className="max-w-xl mx-auto mt-8" data-testid="transfer-complete">
          <Card className="border-green-200 bg-green-50/30">
            <CardContent className="p-8 text-center space-y-4">
              <CheckCircle className="w-16 h-16 text-green-600 mx-auto" />
              <h2 className="text-xl font-bold text-green-800">Ownership Transferred</h2>
              <p className="text-sm text-green-700">
                Platform ownership has been transferred to <strong>{transferResult?.new_owner?.email}</strong>.
              </p>
              <p className="text-sm text-slate-600">
                Your account has been downgraded to <strong>{transferResult?.old_owner_role || 'Admin'}</strong>.
                You will be redirected shortly.
              </p>
              <Button
                className="bg-[#004B8D] hover:bg-[#003a6d] mt-4"
                onClick={() => { logout(); window.location.href = '/login'; }}
                data-testid="post-transfer-logout"
              >
                Sign Out & Re-Login
              </Button>
            </CardContent>
          </Card>
        </div>
      </PlatformOwnerAppShell>
    );
  }

  return (
    <PlatformOwnerAppShell activeId="transfer-ownership" title="Transfer Ownership">
      <div className="max-w-2xl mx-auto space-y-5" data-testid="transfer-ownership-page">

        <PreviewSecurityBanner />

        {/* Warning Banner */}
        <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-xl" data-testid="transfer-warning">
          <AlertTriangle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-red-800">Sensitive Irreversible Action</p>
            <p className="text-xs text-red-700 mt-1">
              Transferring platform ownership will permanently move all Platform Owner privileges to the new owner.
              You will lose Platform Owner access and be downgraded to Admin. This action is audit-logged and requires
              step-up authentication.
            </p>
          </div>
        </div>

        {/* Current Owner Card */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Crown className="w-5 h-5 text-amber-500" />
              Current Platform Owner
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="h-14 bg-slate-100 rounded-lg animate-pulse" />
            ) : currentOwner ? (
              <div className="flex items-center gap-3 p-3 bg-slate-50 border rounded-lg">
                <div className="w-10 h-10 rounded-full bg-gradient-to-r from-amber-500 to-orange-500 flex items-center justify-center text-white font-bold text-sm">
                  {(currentOwner.full_name || 'O')[0]}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-900 truncate">{currentOwner.full_name || 'Platform Owner'}</p>
                  <p className="text-xs text-slate-500">{currentOwner.email}</p>
                </div>
                <Badge className="bg-purple-100 text-purple-700 shrink-0">Platform Owner</Badge>
              </div>
            ) : (
              <p className="text-sm text-slate-500">Could not load owner info</p>
            )}
          </CardContent>
        </Card>

        {/* Target User Lookup */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <ArrowRightLeft className="w-5 h-5 text-[#004B8D]" />
              Transfer To
            </CardTitle>
            <p className="text-sm text-slate-500 mt-1">
              Enter the email of the verified user who should become the new Platform Owner.
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  type="email"
                  placeholder="newowner@example.com"
                  className="pl-10"
                  value={targetEmail}
                  onChange={e => { setTargetEmail(e.target.value); setTargetUser(null); setTargetError(''); }}
                  data-testid="target-email-input"
                />
              </div>
              <Button
                onClick={handleLookup}
                disabled={validating || !targetEmail.trim()}
                className="bg-[#004B8D] hover:bg-[#003a6d]"
                data-testid="lookup-user-btn"
              >
                {validating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              </Button>
            </div>

            {targetError && (
              <div className="flex items-center gap-2 p-3 bg-red-50 text-red-700 rounded-lg text-sm" data-testid="target-error">
                <XCircle className="w-4 h-4 shrink-0" />
                {targetError}
              </div>
            )}

            {targetUser && (
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg space-y-3" data-testid="target-user-card">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#004B8D] flex items-center justify-center text-white font-bold text-sm">
                    {(targetUser.full_name || 'U')[0]}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-900">{targetUser.full_name}</p>
                    <p className="text-xs text-slate-500">{targetUser.email}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <Badge variant="outline" className="text-xs">{roleLabel(targetUser.roles)}</Badge>
                    <p className="text-[10px] text-slate-400 mt-0.5">
                      ID: {targetUser.verification_status === 'verified' ? 'Verified' : targetUser.verification_status}
                    </p>
                  </div>
                </div>

                <Button
                  className="w-full bg-red-600 hover:bg-red-700 text-white"
                  onClick={handleInitiateTransfer}
                  data-testid="initiate-transfer-btn"
                >
                  <ArrowRightLeft className="w-4 h-4 mr-2" />
                  Transfer Ownership to {targetUser.full_name || targetUser.email}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Final Confirmation Modal */}
      {showConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="transfer-confirm-modal">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowConfirm(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
            <div className="bg-red-600 px-6 py-4 flex items-center gap-3 text-white">
              <AlertTriangle className="w-5 h-5" />
              <h3 className="font-semibold">Confirm Ownership Transfer</h3>
            </div>
            <div className="p-6 space-y-4">
              <div className="space-y-2 text-sm text-slate-700">
                <p>You are about to transfer <strong>Platform Owner</strong> access:</p>
                <div className="p-3 bg-slate-50 rounded-lg space-y-1">
                  <p className="flex items-center gap-2">
                    <span className="text-slate-400">From:</span>
                    <strong>{currentOwner?.full_name}</strong> ({currentOwner?.email})
                  </p>
                  <p className="flex items-center gap-2">
                    <span className="text-slate-400">To:</span>
                    <strong>{targetUser?.full_name}</strong> ({targetUser?.email})
                  </p>
                </div>
                <div className="space-y-1.5 text-xs text-slate-600 bg-amber-50 p-3 rounded-lg border border-amber-200">
                  <p className="flex items-start gap-1.5">
                    <AlertTriangle className="w-3.5 h-3.5 text-amber-600 shrink-0 mt-0.5" />
                    <span><strong>{targetUser?.full_name}</strong> will receive full Platform Owner privileges.</span>
                  </p>
                  <p className="flex items-start gap-1.5">
                    <AlertTriangle className="w-3.5 h-3.5 text-amber-600 shrink-0 mt-0.5" />
                    <span><strong>You</strong> will lose Platform Owner access and be downgraded to Admin.</span>
                  </p>
                  <p className="flex items-start gap-1.5">
                    <ShieldCheck className="w-3.5 h-3.5 text-amber-600 shrink-0 mt-0.5" />
                    <span>This action will be permanently recorded in the audit log.</span>
                  </p>
                </div>
              </div>
              <div className="flex gap-3">
                <Button variant="outline" className="flex-1" onClick={() => setShowConfirm(false)}>
                  Cancel
                </Button>
                <Button
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                  onClick={handleConfirmTransfer}
                  disabled={transferring}
                  data-testid="confirm-transfer-btn"
                >
                  {transferring ? 'Transferring...' : 'Confirm & Authenticate'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Step-Up Auth */}
      <StepUpAuthModal
        isOpen={showStepUp}
        onSuccess={(token) => { setShowStepUp(false); executeTransfer(token); }}
        onCancel={() => setShowStepUp(false)}
        actionLabel="transfer platform ownership"
      />
    </PlatformOwnerAppShell>
  );
}
